import type { Properties } from 'hast';
import type { Root } from 'mdast';
import { ContainerDirective, TextDirective } from 'mdast-util-directive';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

interface DirectiveDataWithHast {
  hName?: string;
  hProperties?: Properties;
  [key: string]: unknown;
}

/**
 * Remark plugin that transforms image directives into Image components
 *
 * Supports both block (container) and inline (text) image directives:
 *
 * Block syntax (figure with caption):
 * ```markdown
 * :::image{src="data:image/png;base64,..." alt="Chart" format="png"}
 * :::
 * ```
 *
 * Inline syntax (inline image in paragraph):
 * ```markdown
 * The chart :image{src="data:image/png;base64,..." alt="Chart"} shows growth.
 * ```
 *
 * Attributes:
 * - src: Data URI for the image (required) - e.g., "data:image/png;base64,..."
 * - alt: Alternative text for accessibility (optional)
 * - format: Image format (png, svg, jpg) (optional)
 *
 * @example
 * ```markdown
 * :::image{src="data:image/png;base64,iVBORw0..." alt="Revenue Chart" format="png"}
 * :::
 * ```
 */
export const remarkImage: Plugin<[], Root> = () => (tree) => {
  const visitDirective = (node: ContainerDirective | TextDirective) => {
    if (node.name !== 'image') {
      return;
    }

    // Extract attributes
    const src = node.attributes?.src ? String(node.attributes.src) : '';
    const alt = node.attributes?.alt ? String(node.attributes.alt) : '';
    const format = node.attributes?.format
      ? String(node.attributes.format)
      : undefined;

    // Validate src
    if (!src) {
      return;
    }

    // Validate data URI format
    if (!src.startsWith('data:image/')) {
      return;
    }

    // Determine if this is a block or inline image
    const isBlock = node.type === 'containerDirective';

    // Transform the directive node into an image component node
    // react-markdown will map this to the Image React component via the components prop
    const data = (node.data || {}) as DirectiveDataWithHast;

    // Use different component names for block vs inline
    data.hName = isBlock ? 'imageBlock' : 'imageInline';
    data.hProperties = {
      src,
      alt,
      format,
      inline: !isBlock,
    };
    node.data = data;

    // Clear children since we've extracted the data
    node.children = [];
  };

  // Handle both container and text directives
  visit(tree, 'containerDirective', visitDirective);
  visit(tree, 'textDirective', visitDirective);
};
